import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { API_URL } from '../utils/apiConfig';

const EmployeePay = ({ employee, onClose, onSuccess }) => {
  const { userRole } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    position: employee.position || '',
    payRate: employee.payRate || '',
    note: ''
  });
  
  const [originalData] = useState({
    position: employee.position || '',
    payRate: employee.payRate || ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [changes, setChanges] = useState([]);

  // Check if user can update pay
  const canUpdate = userRole === 'admin' || userRole === 'manager';

  useEffect(() => {
    // Track changes
    const changesList = [];
    
    if (formData.position !== originalData.position && formData.position) {
      changesList.push({
        type: 'position',
        from: originalData.position || 'Not set',
        to: formData.position
      });
    }
    
    const newRate = parseFloat(formData.payRate);
    const oldRate = parseFloat(originalData.payRate);
    
    if (newRate !== oldRate && formData.payRate) {
      changesList.push({
        type: 'payRate',
        from: oldRate || 0,
        to: newRate,
        direction: newRate > oldRate ? 'increase' : 'decrease',
        difference: (newRate - oldRate).toFixed(2)
      });
    }
    
    setChanges(changesList);
  }, [formData, originalData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (changes.length === 0) {
      setError('No changes detected. Please modify the position or pay rate.');
      return;
    }

    if (!formData.note.trim()) {
      setError('Please provide a note explaining the reason for this change.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/employees/${employee.id}/pay-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          position: formData.position,
          payRate: formData.payRate,
          note: formData.note,
          changes: changes
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update employee pay');
      }

      // Call onSuccess with updated values so parent can update UI immediately
      if (onSuccess) {
        onSuccess({
          message: 'Employee pay information updated successfully',
          updated: {
            position: formData.position,
            payRate: parseFloat(formData.payRate)
          }
        });
      }
      if (onClose) {
        onClose();
      }
    } catch (err) {
      setError(err.message || 'Failed to update pay information');
    } finally {
      setLoading(false);
    }
  };

  const getPayChangeIcon = () => {
    const payChange = changes.find(c => c.type === 'payRate');
    if (!payChange) return <MonetizationOnIcon />;
    
    return payChange.direction === 'increase' ? 
      <TrendingUpIcon color="success" /> : 
      <TrendingDownIcon color="error" />;
  };

  const getPayChangeColor = () => {
    const payChange = changes.find(c => c.type === 'payRate');
    if (!payChange) return 'primary';
    return payChange.direction === 'increase' ? 'success' : 'error';
  };

  if (!canUpdate) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <MonetizationOnIcon color="primary" />
            <Typography variant="h6">Update Employee Pay</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error">
            You don't have permission to update employee pay information.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {getPayChangeIcon()}
          <Typography variant="h6">Update Employee Pay</Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {employee.firstName} {employee.lastName}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Current Information */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Current Information
              </Typography>
              <Box display="flex" gap={3}>
                <Typography variant="body2">
                  <strong>Position:</strong> {originalData.position || 'Not set'}
                </Typography>
                <Typography variant="body2">
                  <strong>Pay Rate:</strong> ${originalData.payRate || '0.00'}/hr
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* New Position */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Position/Title"
              name="position"
              value={formData.position}
              onChange={handleChange}
              disabled={loading}
              helperText="Update if employee is promoted or role changes"
            />
          </Grid>

          {/* New Pay Rate */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Pay Rate ($/hr)"
              name="payRate"
              type="number"
              value={formData.payRate}
              onChange={handleChange}
              disabled={loading}
              inputProps={{ 
                step: '0.01',
                min: '0'
              }}
              helperText="Enter the new hourly pay rate"
            />
          </Grid>

          {/* Reason for Change */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reason for Change"
              name="note"
              value={formData.note}
              onChange={handleChange}
              disabled={loading}
              multiline
              rows={3}
              required
              helperText="Provide a detailed explanation for this pay adjustment"
              placeholder="e.g., Annual performance review - exceeds expectations, Promotion to Senior position, Market rate adjustment, etc."
            />
          </Grid>

          {/* Changes Preview */}
          {changes.length > 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Changes to be Applied:
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {changes.map((change, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={1}>
                      {change.type === 'position' && (
                        <>
                          <Chip 
                            label="Position" 
                            size="small" 
                            color="primary" 
                            variant="outlined" 
                          />
                          <Typography variant="body2">
                            {change.from} → <strong>{change.to}</strong>
                          </Typography>
                        </>
                      )}
                      {change.type === 'payRate' && (
                        <>
                          <Chip 
                            label={`Pay ${change.direction === 'increase' ? 'Increase' : 'Decrease'}`}
                            size="small" 
                            color={change.direction === 'increase' ? 'success' : 'error'}
                            variant="outlined"
                          />
                          <Typography variant="body2">
                            ${change.from.toFixed(2)} → <strong>${change.to.toFixed(2)}</strong>
                            {' '}
                            <Typography component="span" variant="body2" color={getPayChangeColor()}>
                              ({change.direction === 'increase' ? '+' : ''}{change.difference})
                            </Typography>
                          </Typography>
                        </>
                      )}
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Important Note */}
          <Grid item xs={12}>
            <Alert severity="info">
              This change will be logged and appear in the employee's history. 
              The employee will be notified and must acknowledge this pay change.
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={loading}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={getPayChangeColor()}
          disabled={loading || changes.length === 0 || !formData.note.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? 'Updating...' : 'Update Pay Information'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeePay;
